import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import cx from 'classnames';
import { useId } from 'utilsTS/hooks';

import { Inline } from 'components/Typography';

import styles from './Switch.module.scss';

export enum SWITCH_SHOW_LABEL {
    NEVER = 'never',
    BEFORE = 'before',
    AFTER = 'after',
}

export interface SwitchProps extends React.ComponentPropsWithoutRef<'button'> {
    children?: never;

    /** Defines active state for programmatic use */
    active?: boolean;
    /** Defines a11y label */
    label?: string;
    /** Defines whether the label should be displayed and its position */
    showLabel?: SWITCH_SHOW_LABEL;
    /** Accepts a tag or a component replacing the wrapper */
    tag?: React.ElementType;
    /** Fires every time the component is toggled */
    onToggle?: (isActive: boolean) => void;
}

export interface SwitchStatics {
    SHOW_LABEL: typeof SWITCH_SHOW_LABEL;
}

export const Switch: React.FC<SwitchProps> & SwitchStatics = ({
    active,
    label,
    showLabel = SWITCH_SHOW_LABEL.NEVER,
    tag: Tag = 'button',
    className,
    disabled,
    id: defaultId,
    onClick = noop,
    onToggle = noop,
    ...rest
}) => {
    const id = useId('switch', defaultId);
    const [activeState, setActive] = React.useState(active);

    React.useEffect(() => {
        setActive(active);
    }, [active]);

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
        event.preventDefault();
        onClick(event);
        onToggle(!activeState);
        setActive(!activeState);

        if (window.navigator.vibrate) {
            window.navigator.vibrate(7);
        }
    };

    const classNames = cx(styles.container, className);
    const isLabelHidden = showLabel === SWITCH_SHOW_LABEL.NEVER;
    const ariaLabel = isLabelHidden ? label : undefined;

    const SwitchComponent = (
        <Tag
            {...rest}
            id={id}
            aria-label={ariaLabel}
            aria-checked={!!activeState}
            className={classNames}
            disabled={disabled}
            role="switch"
            type="button"
            onClick={handleClick}
        >
            <span className={styles.knob} />
        </Tag>
    );

    if (isLabelHidden) {
        return SwitchComponent;
    }

    return (
        <label htmlFor={id} className={styles.label}>
            <Inline className={styles[`label-${showLabel}`]}>{label}</Inline>
            {SwitchComponent}
        </label>
    );
};

Switch.SHOW_LABEL = SWITCH_SHOW_LABEL;

Switch.displayName = 'Switch';
Switch.propTypes = {
    tag: PropTypes.elementType as React.Validator<React.ElementType>,
    active: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.string,
    showLabel: PropTypes.oneOf(Object.values(SWITCH_SHOW_LABEL)),
    onClick: PropTypes.func,
    onToggle: PropTypes.func,
};
